import { createStore } from 'vuex';
import axios from 'axios';
import { roleHasPermission, roleHasAnyPermission } from '../config/permissions';

const CONFIGURATION_SETTINGS_POLL_MS = 5 * 60 * 1000;
let configurationSettingsPollTimer = null;

function clearConfigurationSettingsPoll() {
  if (configurationSettingsPollTimer) {
    clearInterval(configurationSettingsPollTimer);
    configurationSettingsPollTimer = null;
  }
}

export default createStore({
  state: {
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    token: localStorage.getItem('token') || null,
    configurationSettings: [],
    configurationSettingsLoading: false,
    configurationSettingsError: null,
    configurationSettingsLastFetchedAt: null,
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user;
      localStorage.setItem('user', JSON.stringify(user));
    },
    SET_TOKEN(state, token) {
      state.token = token;
      localStorage.setItem('token', token);
    },
    LOGOUT(state) {
      state.user = null;
      state.token = null;
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    },
    SET_CONFIGURATION_SETTINGS(state, items) {
      state.configurationSettings = Array.isArray(items) ? items : [];
      state.configurationSettingsError = null;
      state.configurationSettingsLastFetchedAt = Date.now();
    },
    SET_CONFIGURATION_SETTINGS_LOADING(state, loading) {
      state.configurationSettingsLoading = Boolean(loading);
    },
    SET_CONFIGURATION_SETTINGS_ERROR(state, error) {
      state.configurationSettingsError = error || null;
    },
    CLEAR_CONFIGURATION_SETTINGS(state) {
      state.configurationSettings = [];
      state.configurationSettingsLoading = false;
      state.configurationSettingsError = null;
      state.configurationSettingsLastFetchedAt = null;
    },
  },
  actions: {
    login({ commit, dispatch }, { user, token }) {
      commit('SET_USER', user);
      commit('SET_TOKEN', token);
      dispatch('startConfigurationSettingsPolling');
    },
    logout({ commit }) {
      clearConfigurationSettingsPoll();
      commit('CLEAR_CONFIGURATION_SETTINGS');
      commit('LOGOUT');
    },
    async fetchConfigurationSettings({ commit, state }) {
      if (!state.token) return;
      commit('SET_CONFIGURATION_SETTINGS_LOADING', true);
      try {
        const res = await axios.get('/api/configuration-settings');
        const data = res.data?.data ?? res.data;
        commit('SET_CONFIGURATION_SETTINGS', Array.isArray(data) ? data : []);
      } catch (err) {
        const message =
          err.response?.data?.error || err.message || 'Failed to load configuration settings';
        commit('SET_CONFIGURATION_SETTINGS_ERROR', message);
      } finally {
        commit('SET_CONFIGURATION_SETTINGS_LOADING', false);
      }
    },
    startConfigurationSettingsPolling({ dispatch, state }) {
      clearConfigurationSettingsPoll();
      if (!state.token) return;
      dispatch('fetchConfigurationSettings');
      configurationSettingsPollTimer = setInterval(() => {
        dispatch('fetchConfigurationSettings');
      }, CONFIGURATION_SETTINGS_POLL_MS);
    },
    stopConfigurationSettingsPolling() {
      clearConfigurationSettingsPoll();
    },
  },
  getters: {
    isAuthenticated: (state) => !!state.token,
    user: (state) => state.user,
    userRole: (state) => state.user?.role,
    isAdmin: (state) => state.user?.role === 'admin',
    isManager: (state) => state.user?.role === 'manager',
    isTeamLeader: (state) => state.user?.role === 'team_leader',
    isAgent: (state) => state.user?.role === 'agent',
    isSupport: (state) => {
      const role = state.user?.role;
      return role === 'manager' || role === 'team_leader' || role === 'agent' || role === 'support';
    },
    isViewer: (state) => state.user?.role === 'viewer',
    /** @returns {(key: string) => boolean} */
    hasPermission: (state) => (permissionKey) =>
      roleHasPermission(state.user?.role, permissionKey),
    /** @returns {(keys: string[]) => boolean} */
    hasAnyPermission: (state) => (keys) =>
      roleHasAnyPermission(state.user?.role, keys),

    configurationSettings: (state) => state.configurationSettings,
    configurationSettingsLoading: (state) => state.configurationSettingsLoading,
    configurationSettingsError: (state) => state.configurationSettingsError,
    configurationSettingsLastFetchedAt: (state) => state.configurationSettingsLastFetchedAt,
    configurationSettingsByKeyName: (state) => {
      const map = Object.create(null);
      for (const row of state.configurationSettings) {
        if (row?.key_name) map[row.key_name] = row;
      }
      return map;
    },
    configurationSettingsByKeyId: (state) => {
      const map = Object.create(null);
      for (const row of state.configurationSettings) {
        if (row?.key != null) map[row.key] = row;
      }
      return map;
    },
    /** @returns {(keyName: string) => object|undefined} */
    configurationSettingByKeyName: (_state, getters) => (keyName) =>
      getters.configurationSettingsByKeyName[keyName],
    /** @returns {(keyId: number|string) => object|undefined} */
    configurationSettingByKeyId: (_state, getters) => (keyId) =>
      getters.configurationSettingsByKeyId[keyId],
    /** @returns {(keyName: string) => boolean} */
    isConfigurationActiveByKeyName: (_state, getters) => (keyName) => {
      const row = getters.configurationSettingByKeyName(keyName);
      return row?.status === 'active';
    },
    /** @returns {(keyId: number|string) => boolean} */
    isConfigurationActiveByKeyId: (_state, getters) => (keyId) => {
      const row = getters.configurationSettingByKeyId(keyId);
      return row?.status === 'active';
    },
  }
});
